/**
 * WhoWeAreView - Content type view for "Who we are" pages
 *
 * Layout sections:
 * 1. PageHeaderBackground with title, full_description, and hero_image
 * 2. "What we do" - RichText section
 * 3. Tabs section (Short-term forecast, Climate predictions, Climate projections)
 *    Each tab has RichText content + desktop/mobile timescale images
 * 4. "How we do this" - RichText section
 * 5. "We are a multidisciplinary team" - RichText section
 *
 * @module components/Views/WhoWeAreView
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { RichText } from 'volto-essvoltocomponents/components/Atoms';
import { PageHeaderBackground } from '../../Molecules/PageHeaderBackground';
import Dropdown from '../../Molecules/DropDown/Dropdown';
import TeaserProfile from '../../Molecules/TeaserProfile';
import ResponsiveGrid from '../../Atoms/ResponsiveGrid';
import Pagination from '../../Molecules/Pagination/Pagination';
import { stripHTML } from 'volto-essvoltocomponents/helpers';
import { resolveImageURL } from 'volto-essvoltocomponents/helpers';
import './WhoWeAreView.scss';
import {
  ContentWide,
  ContentNarrow,
  ContentWrapper,
  PageSection,
} from 'volto-essvoltocomponents/components/Layout';

/**
 * Extract HTML from a Plone RichText field.
 * The REST API may return the HTML in .output or .data depending on serializer.
 */
const getRichTextHTML = (field) => {
  if (!field) return null;
  return field.output || field.data || null;
};

const TEAM_GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 450, columns: 2 },
  { minWidth: 600, columns: 3 },
  { minWidth: 1024, columns: 4 },
  { minWidth: 1280, columns: 6 },
];

const TEAM_PER_PAGE = 12;

const WhoWeAreView = ({ content }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('tab1');
  const [teamPage, setTeamPage] = useState(1);

  // Fetch team members from the 'team' subfolder with server-side pagination
  const contentPath = content?.['@id'] ? flattenToAppURL(content['@id']) : '';
  const subrequestKey = `whoWeAreTeam-${contentPath}-${teamPage}`;

  const teamMembers = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.items || [],
  );
  const teamTotal = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.total || 0,
  );
  const teamLoaded = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.loaded || false,
  );

  useEffect(() => {
    if (contentPath && !teamLoaded) {
      dispatch(
        searchContent(
          contentPath,
          {
            portal_type: ['team_member'],
            review_state: 'published',
            sort_on: 'getObjPositionInParent',
            sort_order: 'ascending',
            b_size: TEAM_PER_PAGE,
            b_start: (teamPage - 1) * TEAM_PER_PAGE,
            metadata_fields: ['position'],
          },
          subrequestKey,
        ),
      );
    }
  }, [dispatch, contentPath, teamPage, subrequestKey, teamLoaded]);
  const teamTotalPages = Math.max(1, Math.ceil(teamTotal / TEAM_PER_PAGE));

  // Build hero image URL from content
  const heroImageUrl = resolveImageURL(content, '/@@images/hero_image/hero');

  // Tab definitions mapped to content type fields
  const tabs = [
    {
      id: 'tab1',
      label: 'Short-term forecast',
      content: content?.tab1_contents,
      image: content?.tab1_image,
      imageMobile: content?.tab1_image_m,
    },
    {
      id: 'tab2',
      label: 'Climate predictions',
      content: content?.tab2_contents,
      image: content?.tab2_image,
      imageMobile: content?.tab2_image_m,
    },
    {
      id: 'tab3',
      label: 'Climate projections',
      content: content?.tab3_contents,
      image: content?.tab3_image,
      imageMobile: content?.tab3_image_m,
    },
  ];

  //Handle filter change for mobile dropdown - updates URL query parameter
  const handleItemSelect = function (item) {
    if (item.disabled) return;
    setInternalLabel(item.label); // updaye the button label to the selected item
    setActiveTab(item); // update the active tab to show the selected content
    setOpen(false);
    item.onSelect?.(); // optional side effects only (NOT URL)
  };

  return (
    <div className="who-we-are-view">
      {/* 1. Hero section with PageHeaderBackground */}
      <PageHeaderBackground
        title={content?.title || ''}
        description={
          stripHTML(getRichTextHTML(content?.full_description)) ||
          content?.description ||
          ''
        }
        backgroundImage={heroImageUrl}
        grayscale={100}
        imageOpacity={0.4}
        overlayOpacity={0.3}
      />

      {/* 2. What we do - RichText section */}
      {getRichTextHTML(content?.what_we_do) && (
        <PageSection
          paddingBottom="var(--ess-space-7xl)"
          className="who-we-are-view__section"
        >
          <ContentWrapper>
            <ContentWide alignment="left">
              <ContentNarrow alignment="left">
                <h2 className="who-we-are-view__section-title">What we do</h2>
                <RichText
                  content={getRichTextHTML(content.what_we_do)}
                  className="who-we-are-view__what-we-do"
                />
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* 3a. Tab buttons - on page background (outside blue area) */}
      <PageSection
        className="who-we-are-view__tabs-menu"
        paddingTop="0"
        paddingBottom="0"
      >
        <ContentWrapper>
          <ContentWide alignment="left">
            {/* Desktop: tab buttons */}
            <div
              role="tablist"
              className="who-we-are-view__tablist who-we-are-view__tablist--desktop"
            >
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  role="tab"
                  id={tab.id}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`${tab.id}-panel`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`who-we-are-view__tab-button${
                    activeTab === tab.id
                      ? ' who-we-are-view__tab-button--selected'
                      : ''
                  }${index === 0 ? ' who-we-are-view__tab-button--first' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Mobile: dropdown select */}
            <div className="who-we-are-view__tablist--mobile">
              <p className="who-we-are-view__tablist-title">
                Select a category to explore:{' '}
              </p>
              {/* Stateless dropdown, only renders based on props and calls a callback owned by the parent component */}
              <Dropdown
                label={tabs.find((item) => item.id === activeTab)?.label}
                items={tabs.map((item) => ({
                  id: item.id,
                  label: item.label,
                  onSelect: () => setActiveTab(item.id),
                }))}
                className="who-we-are-view__dropdown"
              />
            </div>
          </ContentWide>
        </ContentWrapper>
      </PageSection>

      {/* 3b. Tab panels - blue background */}
      <PageSection
        className="who-we-are-view__tabs-section"
        backgroundColor="var(--tabs-variantdefault-statedefault-bgcolor)"
      >
        <ContentWrapper>
          <ContentWide alignment="left">
            <ContentNarrow alignment="left">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  id={`${tab.id}-panel`}
                  role="tabpanel"
                  aria-labelledby={tab.id}
                  hidden={activeTab !== tab.id}
                  className="who-we-are-view__tab-panel"
                >
                  {/* Tab RichText content */}
                  {getRichTextHTML(tab.content) && (
                    <>
                      <h2 className="who-we-are-view__tab-label">
                        {tab.label}
                      </h2>
                      <RichText
                        content={getRichTextHTML(tab.content)}
                        className="who-we-are-view__tab-content"
                      />
                    </>
                  )}

                  {/* Timescale image - desktop */}
                  {tab.image?.download && (
                    <img
                      src={flattenToAppURL(tab.image.download)}
                      alt={tab.label}
                      className="who-we-are-view__tab-image who-we-are-view__tab-image--desktop"
                    />
                  )}

                  {/* Timescale image - mobile */}
                  {tab.imageMobile?.download && (
                    <img
                      src={flattenToAppURL(tab.imageMobile.download)}
                      alt={`${tab.label} (mobile)`}
                      className="who-we-are-view__tab-image who-we-are-view__tab-image--mobile"
                    />
                  )}
                </div>
              ))}
            </ContentNarrow>
          </ContentWide>
        </ContentWrapper>
      </PageSection>

      {/* 4. How we do this - RichText section */}
      {getRichTextHTML(content?.how_we_do_it) && (
        <PageSection className="who-we-are-view__section">
          <ContentWrapper>
            <ContentWide alignment="left">
              <ContentNarrow alignment="left">
                <h2 className="who-we-are-view__section-title">
                  How we do this
                </h2>
                <RichText
                  content={getRichTextHTML(content.how_we_do_it)}
                  className="who-we-are-view__how-we-do-it"
                />
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* 5. We are a multidisciplinary team - RichText + team grid */}
      <PageSection className="who-we-are-view__section">
        <ContentWrapper>
          <ContentWide alignment="left">
            <ContentNarrow alignment="left">
              {getRichTextHTML(content?.we_are_a_multidisciplinary_team) && (
                <>
                  <h2 className="who-we-are-view__section-title">
                    We are an interdisciplinary team...
                  </h2>
                  <RichText
                    content={getRichTextHTML(
                      content.we_are_a_multidisciplinary_team,
                    )}
                    className="who-we-are-view__multidisciplinary"
                  />
                </>
              )}
            </ContentNarrow>
          </ContentWide>
          {teamMembers.length > 0 && (
            <ContentWide>
              <div className="who-we-are-view__team-section">
                <ResponsiveGrid
                  breakpoints={TEAM_GRID_BREAKPOINTS}
                  className="who-we-are-view__team-grid"
                >
                  {teamMembers.map((member) => {
                    const picUrl = resolveImageURL(
                      member,
                      '/@@images/picture/preview',
                    );
                    const name = stripHTML(
                      getRichTextHTML(member.name) || member.title || '',
                    );
                    const position = stripHTML(
                      getRichTextHTML(member.position) || '',
                    );
                    return (
                      <TeaserProfile
                        key={member['@id']}
                        imageSrc={picUrl}
                        imageAlt={name}
                        name={name}
                        position={position}
                      />
                    );
                  })}
                </ResponsiveGrid>

                {teamTotalPages > 1 && (
                  <div className="who-we-are-view__team-pagination">
                    <Pagination
                      currentPage={teamPage}
                      totalPages={teamTotalPages}
                      onPageChange={setTeamPage}
                    />
                  </div>
                )}
              </div>
            </ContentWide>
          )}
        </ContentWrapper>
      </PageSection>
    </div>
  );
};

export default WhoWeAreView;
